import { useQuery } from '@tanstack/react-query';
import { Receipt, Users, type LucideIcon } from 'lucide-react';
import { useDeferredValue } from 'react';
import { customers } from '@/api/customers';
import { invoices } from '@/api/invoices';
import { tokenStore } from '@/auth/token-store';
import { useSession } from '@/auth/session-context';
import { formatMoney } from '@/lib/money';

export interface PaletteResultGroup {
  heading: string;
  icon: LucideIcon;
  items: { title: string; subtitle?: string; path: string }[];
}

const INVOICE_NUMBER = /^[A-Z]{2,5}-?\d{2,}$/i;

/**
 * Live search for the command palette: customers by name/email (`search`)
 * and invoices by number. Endpoints that are not live yet simply return
 * nothing; the palette never errors.
 */
export function usePaletteSearch(term: string): { groups: PaletteResultGroup[]; isFetching: boolean } {
  const deferred = useDeferredValue(term.trim());
  const { can } = useSession();
  const enabled = deferred.length >= 2;

  const customerQuery = useQuery({
    queryKey: [tokenStore.scopeId(), 'palette', 'customers', deferred],
    queryFn: ({ signal }) => customers.api.list({ search: deferred, limit: 6 }, signal),
    enabled: enabled && can('customers:read'),
    staleTime: 30_000,
    retry: false,
  });

  const looksLikeNumber = INVOICE_NUMBER.test(deferred);
  const invoiceQuery = useQuery({
    queryKey: [tokenStore.scopeId(), 'palette', 'invoices', deferred],
    queryFn: ({ signal }) => invoices.api.list({ number: deferred.toUpperCase(), limit: 5 }, signal),
    enabled: enabled && looksLikeNumber && can('invoices:read'),
    staleTime: 30_000,
    retry: false,
  });

  const groups: PaletteResultGroup[] = [];
  const customerRows = customerQuery.data?.data ?? [];
  if (enabled && customerRows.length > 0) {
    groups.push({
      heading: 'Customers',
      icon: Users,
      items: customerRows.map((c) => ({
        title: c.name,
        ...(c.email ? { subtitle: c.email } : {}),
        path: `/customers/${c.id}`,
      })),
    });
  }
  const invoiceRows = invoiceQuery.data?.data ?? [];
  if (enabled && invoiceRows.length > 0) {
    groups.push({
      heading: 'Invoices',
      icon: Receipt,
      items: invoiceRows.map((inv) => ({
        title: inv.number ?? 'Draft invoice',
        subtitle: formatMoney(inv.total_minor, inv.currency),
        path: `/invoices/${inv.id}`,
      })),
    });
  }

  return { groups, isFetching: customerQuery.isFetching || invoiceQuery.isFetching };
}
